import type React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui'

interface ChartContainerProps {
  title: string
  description?: string
  children: React.ReactNode
  className?: string
  isLoading?: boolean
  error?: string | null
}

export function ChartContainer({
  title,
  description,
  children,
  className = '',
  isLoading = false,
  error = null,
}: ChartContainerProps) {
  if (isLoading) {
    return <ChartContainerSkeleton title={title} description={description} />
  }

  if (error) {
    return <ChartContainerError title={title} description={description} error={error} />
  }

  return (
    <Card className={`border-0 shadow-sm hover:shadow-md transition-shadow ${className}`}>
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">{title}</CardTitle>
        {description && (
          <CardDescription className="text-sm text-muted-foreground">{description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-2">
        <div className="w-full h-64">{children}</div>
      </CardContent>
    </Card>
  )
}

function ChartContainerSkeleton({ title, description }: { title: string; description?: string }) {
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">{title}</CardTitle>
        {description && (
          <CardDescription className="text-sm text-muted-foreground">{description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-2">
        <div className="w-full h-64 bg-muted rounded-lg animate-pulse flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <div className="w-8 h-8 bg-muted-foreground/20 rounded-full animate-pulse mx-auto mb-2" />
            <div className="text-sm">Carregando gráfico...</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function ChartContainerError({
  title,
  description,
  error,
}: {
  title: string
  description?: string
  error: string
}) {
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">{title}</CardTitle>
        {description && (
          <CardDescription className="text-sm text-muted-foreground">{description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-2">
        <div className="w-full h-64 border-2 border-dashed border-error/20 rounded-lg flex items-center justify-center">
          <div className="text-center text-error/70">
            <div className="text-2xl mb-2">⚠️</div>
            <div className="font-medium mb-1">Erro ao carregar gráfico</div>
            <div className="text-xs text-muted-foreground">{error}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
